import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import React from "react";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutline";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import BuildingIcon from "../assets/Images/building-icon.svg";
import Avatar from "@mui/material/Avatar";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import { useParams } from "react-router-dom";
import { useEffect } from "react";
import { useState } from "react";
import { getInitials } from "../utils/utils";
import bagIcon from "../assets/Images/bag-icon.svg";
import locationIcon from "../assets/Images/location-icon.svg";
import clockIcon from "../assets/Images/clock-icon.svg";
import DownloadCVModal from "../components/DownloadCVModal";

/* Reusable Card Component */
function InfoCard({ title, icon, items }) {
  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography fontWeight={600}>{title}</Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2, gap: 1 }}>
          {items.map((item, index) => (
            <Chip key={index} label={item} size="small" />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


const DeveloperDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [developer, setDeveloper] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [downloaderName, setDownloaderName] = useState("");
  const [downloaderEmail, setDownloaderEmail] = useState("");
  const { id } = useParams();

  useEffect(() => {
    const fetchDeveloper = async () => {
      try {
        const response = await API.get(
          `${api_paths.DEVELOPER.getDetail}/${id}`,
        );
        console.log("Developer Detail:", response.data);

        const developerData = response?.data?.payload;

        setDeveloper(developerData);
      } catch (error) {
        console.error("Error fetching developer:", error);
      }
    };

    fetchDeveloper();
  }, [id]);

  const handleModalSubmit = (name, email) => {
    setDownloaderName(name);
    setDownloaderEmail(email);
    setOpenDownloadModal(false);
    
    // We need to call handleDownload with the specific name/email because 
    // state might not have updated yet when we call it immediately.
    // However, looking at handleDownload, it uses downloaderName and downloaderEmail from state.
    // I'll adjust handleDownload to accept name/email or use state as fallback.
    handleDownload(name, email);
  };

  const handleDownload = async (name = downloaderName, email = downloaderEmail) => {
    try {
      setIsDownloading(true);

      // Construct exact payload format expected by the backend
      // Because our GET API populated some fields into objects instead of IDs/arrays
      const payload = {
        ...developer,
        downloaderName: name.trim(),
        downloaderEmail: email.trim(),

        company: developer?.company?._id || developer?.company,
        expertise: developer?.expertise
          ? [developer.expertise._id || developer.expertise]
          : [],
        nationality: developer?.nationality?._id || developer?.nationality,
        religion: developer?.religion?._id || developer?.religion,
        languages: Array.isArray(developer?.languages)
          ? developer.languages
          : developer?.languages
            ? [developer.languages]
            : [],
        skills: Array.isArray(developer?.skills)
          ? developer.skills
          : developer?.skills
            ? [developer.skills]
            : [],
        tools: Array.isArray(developer?.tools)
          ? developer.tools
          : developer?.tools
            ? [developer.tools]
            : [],
        projects: Array.isArray(developer?.projects)
          ? developer.projects
          : developer?.projects
            ? [developer.projects]
            : [],
      };

      const response = await API.post(
        api_paths.DEVELOPER.generatePreview,
        payload,
        { responseType: "blob" },
      );

      // Create blob link to download
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const downloadUrl = window.URL.createObjectURL(blob);

      // Extract filename from header or fallback
      const contentDisposition = response.headers["content-disposition"];
      let fileName = `${developer?.name || "Developer"}_Profile`;
      const contentType = response.headers["content-type"];

      if (contentType && contentType.includes("application/pdf")) {
        fileName += ".pdf";
      } else if (contentType && contentType.includes("text/html")) {
        fileName += ".html";
      } else {
        fileName += ".pdf"; // Default to pdf if type is unknown but we expect a profile
      }

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      // Only open preview in new tab on desktop. Mobile browsers don't handle PDF iframes well and often leave a blank tab.
      const isMobile = window.innerWidth <= 768 || /Mobi|Android/i.test(navigator.userAgent);
      if (!isMobile) {
        // Open in new tab with the correct title instead of raw blobUrl
        const pdfWindow = window.open("");
        if (pdfWindow) {
          pdfWindow.document.write(`
                      <html>
                          <head>
                              <title>${fileName}</title>
                              <style>
                                  body { margin: 0; padding: 0; overflow: hidden; background-color: #323639; }
                                  iframe { width: 100vw; height: 100vh; border: none; }
                              </style>
                          </head>
                          <body>
                              <iframe src="${downloadUrl}#toolbar=1&navpanes=0&scrollbar=0" title="${fileName}"></iframe>
                          </body>
                      </html>
                  `);
          pdfWindow.document.close();
        } else {
          window.open(downloadUrl, "_blank");
        }
      }

      // Auto download via blob
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 10000);
    } catch (error) {
      console.error("Error downloading profile:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 999,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
        open={isDownloading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">Generating Profile PDF...</Typography>
      </Backdrop>

      <Box
        sx={{ mt: 10, pt: 2, pb: 4, bgcolor: "#f8fafc", minHeight: "100vh" }}
      >
        {/* Back Button */}
        <Container maxWidth="xl" sx={{ mb: 2 }}>
          <Box
            onClick={() => navigate("/engineers")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              color: "#64748b",
              fontSize: 14,
              "&:hover": { color: "#0f172a" },
            }}
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: 18 }} />
            Back to Developers
          </Box>
        </Container>

        <Divider />

        <Container maxWidth="xl" sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            {/* LEFT COLUMN */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={3}>
                {/* Profile Card */}
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack direction="row" spacing={3}>
                      <Avatar
                        sx={{
                          width: 70,
                          height: 70,
                          bgcolor: "#0f172a",
                          fontSize: 22,
                        }}
                      >
                        {getInitials(developer?.name)}
                      </Avatar>

                      <Box>
                        <Typography variant="h5" fontWeight={600}>
                          {developer?.name}
                        </Typography>
                        <Typography
                          color={theme.palette.custom.developerRoleColor}
                          sx={{ mb: 1 }}
                        >
                          {developer?.expertise?.name}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={3}
                          sx={{ color: "#64748b", fontSize: 14, my: 2 }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <img src={bagIcon} alt="bag" />
                            <span>
                              {developer?.experience || 0} years experience
                            </span>
                          </Box>

                          {developer?.nationality?.name && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <img src={locationIcon} alt="location" />
                              <span>{developer?.nationality?.name || ""}</span>
                            </Box>
                          )}

                          {(developer?.timeFrom || developer?.timeTill) && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <img src={clockIcon} alt="clock" />
                              <span>
                                {developer?.timeFrom || "00:00"} -{" "}
                                {developer?.timeTill || "00:00"}
                              </span>
                            </Box>
                          )}
                        </Stack>

                        {/* {developer?.qualification && (
                                                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                                        <Chip label={developer?.qualification}
                                                            sx={{
                                                                backgroundColor: "rgb(220 252 231 / 1)",
                                                                color: "rgb(22 101 52 / 1)",
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    </Stack>
                                                )} */}
                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                          <Chip
                            label="Full-time"
                            sx={{
                              backgroundColor: "rgb(220 252 231 / 1)",
                              color: "rgb(22 101 52 / 1)",
                              fontWeight: 500,
                            }}
                          />
                          <Chip
                            label="Full-stack"
                            sx={{
                              backgroundColor: theme.palette.background.muted,
                              fontWeight: 500,
                              color: "text.secondary",
                              borderRadius: "999px",
                            }}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Professional Summary */}
                <Card sx={{ borderRadius: 3, px: 1 }}>
                  <CardContent>
                    <Typography
                      fontWeight={600}
                      sx={{
                        mb: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PersonOutlineOutlinedIcon className="developer-icons-color" />
                      <span>Professional Summary</span>
                    </Typography>
                    <Typography
                      color="text.secondary"
                      className="leading-relaxed"
                    >
                      {developer?.summary}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Work Experience */}
                {/* <Card sx={{ borderRadius: 3, px: 1 }}>
                  <CardContent>
                    <Typography
                      fontWeight={600}
                      sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <img src={BuildingIcon} alt="building" width={20} />
                      <span>Work Experience</span>
                    </Typography>

                    <Stack spacing={3}>
                      {developer?.experienceDetails &&
                        developer?.experienceDetails?.map((exp, index) => (
                          <Box
                            key={index}
                            sx={{
                              position: "relative",
                              pl: 3,
                              borderLeft: "2px solid",
                              borderColor: "hsl(var(--border))",
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                left: -9,
                                top: 2,
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                border: "2px solid",
                                borderColor: "hsl(var(--accent))", // circle border color
                                bgcolor: "background.paper",
                              }}
                            />

                            <Stack
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Typography fontWeight={600}>
                                {exp?.tech}
                              </Typography>

                              <Chip
                                label={`${exp?.duration} Years`}
                                size="small"
                              />
                            </Stack>
                          </Box>
                        ))}
                    </Stack>
                  </CardContent>
                </Card> */}

                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography
                      fontWeight={600}
                      sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <FolderOpenOutlinedIcon className="developer-icons-color" />
                      Project Experience
                    </Typography>

                    <Stack spacing={3}>
                      {developer?.projects &&
                        developer?.projects?.map((project, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              bgcolor: "grey.50",
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Box>
                                <Typography fontWeight={600}>
                                  {project?.name}
                                </Typography>
                                {project?.description && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {project?.description}
                                  </Typography>
                                )}
                              </Box>

                              {project?.role && (
                                <Chip
                                  label={project?.role}
                                  size="small"
                                  sx={{
                                    backgroundColor: "hsl(var(--accent) / .1)",
                                    color: "hsl(var(--accent))",
                                    height: 24,
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                            </Stack>

                            {(() => {
                              let tools = [];
                              if (Array.isArray(project?.toolsAndTechnology)) {
                                tools = project.toolsAndTechnology;
                              } else if (
                                typeof project?.toolsAndTechnology === "string"
                              ) {
                                try {
                                  tools = JSON.parse(
                                    project.toolsAndTechnology,
                                  );
                                } catch (e) {
                                  console.error(
                                    "Failed to parse toolsAndTechnology",
                                    e,
                                  );
                                }
                              }

                              return Array.isArray(tools) &&
                                tools.length > 0 ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  flexWrap="wrap"
                                  sx={{ mt: 2 }}
                                >
                                  {tools.map((tech, i) => (
                                    <Chip
                                      key={i}
                                      label={String(tech)}
                                      size="small"
                                      className="developer-attributes"
                                    />
                                  ))}
                                </Stack>
                              ) : null;
                            })()}
                          </Box>
                        ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={3}>
                <Card
                  sx={{
                    borderRadius: 3,
                    bgcolor: "#0f172a",
                    color: "white",
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography fontWeight={600} color="white">
                      Interested in this developer?
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, mb: 2, opacity: 0.8 }}
                    >
                      Download the full profile to share with your team.
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={
                        isDownloading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      sx={{
                        bgcolor: "#14b8a6",
                        "&:hover": { bgcolor: "#0d9488" },
                      }}
                      onClick={() => setOpenDownloadModal(true)}
                      disabled={isDownloading}
                    >
                      {isDownloading
                        ? "Downloading..."
                        : "Download Profile (PDF)"}
                    </Button>
                  </CardContent>
                </Card>

                {/* Skills Card */}
                {Array.isArray(developer?.skills) &&
                  developer.skills.length > 0 && (
                    <InfoCard
                      title="Skills"
                      icon={
                        <CodeOutlinedIcon className="developer-icons-color" />
                      }
                      items={developer.skills}
                    />
                  )}

                {Array.isArray(developer?.tools) &&
                  developer.tools.length > 0 && (
                    <InfoCard
                      title="Tools & Software"
                      icon={
                        <BuildOutlinedIcon className="developer-icons-color" />
                      }
                      items={developer.tools.map((t) => t?.name || "")}
                    />
                  )}

                {Array.isArray(developer?.languages) &&
                  developer.languages.length > 0 && (
                    <Card
                      sx={{
                        borderRadius: 3,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        p: 1,
                      }}
                    >
                      <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TranslateOutlinedIcon
                            sx={{ color: "#0ea5a4", fontSize: 22 }}
                          />
                          <Typography fontWeight={600} fontSize={18}>
                            Languages
                          </Typography>
                        </Stack>

                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                          {developer.languages.map((lang, index) => (
                            <Stack
                              key={index}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <CheckCircleOutlineOutlinedIcon
                                sx={{ fontSize: 18 }}
                                className="developer-icons-color"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {lang?.id?.name ||
                                  lang?.name ||
                                  `Language ${index + 1}`}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                <Card
                  sx={{
                    p: 1,
                    borderRadius: 3,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  <CardContent>
                    <Typography
                      fontWeight={600}
                      fontSize={18}
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <FolderOpenOutlinedIcon className="developer-icons-color" />
                      Education
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
                      {developer?.qualification}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {developer?.college}{" "}
                      {developer?.collegeCity
                        ? `- ${developer?.collegeCity}`
                        : ""}
                    </Typography>
                    {developer?.graduationDate && (
                      <Typography variant="body2" color="text.secondary">
                        Graduated:{" "}
                        {new Date(
                          developer?.graduationDate,
                        ).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <DownloadCVModal
        open={openDownloadModal}
        onClose={() => setOpenDownloadModal(false)}
        onSubmit={handleModalSubmit}
        loading={isDownloading}
        developerName={developer?.name}
      />
    </>
  );
};

export default DeveloperDetail;
